import { useState, useRef, useEffect, KeyboardEvent, ChangeEvent } from "react";
import { useChatStore, Message } from "@/stores/chat.store";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Export Message type for reuse
export type { Message };

interface Attachment {
  id: string;
  type: "image" | "document" | "audio" | "video" | "other";
  name: string;
  originalName: string;
  url: string;
  size: number;
  mimeType: string;
  file?: File;
}

// Props for external/portal usage
interface ExternalActionProps {
  onSendMessage?: (
    conversationId: string,
    content: string,
    options?: {
      attachments?: Omit<Attachment, "file">[];
      replyTo?: string;
    },
  ) => Promise<void>;
  onStartTyping?: (conversationId: string) => void;
  onStopTyping?: (conversationId: string) => void;
  onUploadFile?: (file: File) => Promise<{
    id: string;
    filename: string;
    originalName: string;
    url: string;
    size: number;
    mimeType: string;
  } | null>;
  showTypingIndicator?: boolean;
}

interface MessageInputProps extends ExternalActionProps {
  conversationId: string;
  disabled?: boolean;
  replyTo?: Message | null;
  onCancelReply?: () => void;
  /** Pass to enable AI smart-reply & follow-up suggestions */
  conversationContext?: {
    title?: string;
    type?: string;
    recentMessages?: Pick<Message, "content" | "sender" | "createdAt">[];
    currentUserId?: string;
  };
}

export function MessageInput({
  conversationId,
  disabled,
  replyTo,
  onCancelReply,
  conversationContext,
  // External action props (for portal usage)
  onSendMessage: externalSendMessage,
  onStartTyping: externalStartTyping,
  onStopTyping: externalStopTyping,
  onUploadFile: externalUploadFile,
  showTypingIndicator = true,
}: MessageInputProps) {
  // Determine if using external handlers
  const useExternalHandlers = externalSendMessage !== undefined;

  const [message, setMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [followUpVisible, setFollowUpVisible] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const chatStore = useChatStore();

  // Resolve actions from props or store
  const sendMessage = useExternalHandlers
    ? externalSendMessage
    : chatStore.sendMessage;
  const startTyping =
    useExternalHandlers && externalStartTyping
      ? externalStartTyping
      : chatStore.startTyping;
  const stopTyping =
    useExternalHandlers && externalStopTyping
      ? externalStopTyping
      : chatStore.stopTyping;

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  }, [message]);

  // Stop typing when component unmounts or conversation changes
  useEffect(() => {
    return () => {
      if (isTyping) {
        stopTyping(conversationId);
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [conversationId, isTyping, stopTyping]);

  // Show follow-up banner when the current user sent the last message >4h ago
  useEffect(() => {
    if (!conversationContext?.recentMessages?.length || !conversationContext.currentUserId) {
      setFollowUpVisible(false);
      return;
    }
    const msgs = conversationContext.recentMessages;
    const last = msgs[msgs.length - 1];
    const isOwnLast =
      typeof last.sender?.userId === "string"
        ? last.sender.userId === conversationContext.currentUserId
        : String(last.sender?.userId) === conversationContext.currentUserId;
    if (!isOwnLast) { setFollowUpVisible(false); return; }
    const hoursSince = (Date.now() - new Date(last.createdAt).getTime()) / 3_600_000;
    setFollowUpVisible(hoursSince >= 4);
  }, [conversationContext]);

  const buildPreviousMessages = () => {
    if (!conversationContext?.recentMessages?.length) return "";
    return conversationContext.recentMessages
      .slice(-8)
      .map((m) => `${m.sender?.displayName ?? "User"}: ${m.content}`)
      .join("\n");
  };

  const handleAiSuggest = async (intent: string) => {
    setAiLoading(true);
    try {
      const res = await api.postAi<{ subject?: string; body: string }>(
        "/ai/compose",
        {
          context: conversationContext?.title
            ? `Conversation: ${conversationContext.title} (${conversationContext.type ?? "chat"})`
            : "Team chat conversation",
          intent,
          tone: "friendly",
          previousMessages: buildPreviousMessages(),
          entityType: "chat",
        },
      );
      const text = (res as any).data?.draft ?? (res as any).data?.body ?? (res as any).data?.text ?? "";
      if (!text) {
        toast.error("AI returned empty — add more context first");
        return;
      }
      setMessage(text);
      textareaRef.current?.focus();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message ?? "AI suggestion failed");
    } finally {
      setAiLoading(false);
    }
  };

  const handleTyping = () => {
    if (!showTypingIndicator) return;

    if (!isTyping) {
      setIsTyping(true);
      startTyping(conversationId);
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      stopTyping(conversationId);
    }, 3000);
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    handleTyping();
  };

  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);

    const newAttachments: Attachment[] = [];

    for (const file of Array.from(files)) {
      // If external upload function is provided, upload file to server
      if (externalUploadFile) {
        try {
          const uploadResult = await externalUploadFile(file);
          if (uploadResult) {
            newAttachments.push({
              id: uploadResult.id,
              type: getFileType(uploadResult.mimeType),
              name: uploadResult.filename,
              originalName: uploadResult.originalName,
              url: uploadResult.url,
              size: uploadResult.size,
              mimeType: uploadResult.mimeType,
            });
          }
        } catch (error) {
          console.error("Failed to upload file:", error);
        }
      } else {
        // Fallback to local blob URL (for internal chat)
        const url = URL.createObjectURL(file);
        const type = getFileType(file.type);

        newAttachments.push({
          id: crypto.randomUUID(),
          type,
          name: file.name,
          originalName: file.name,
          url,
          size: file.size,
          mimeType: file.type,
          file,
        });
      }
    }

    setAttachments((prev) => [...prev, ...newAttachments]);
    setIsUploading(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const getFileType = (mimeType: string): Attachment["type"] => {
    if (mimeType.startsWith("image/")) return "image";
    if (mimeType.startsWith("video/")) return "video";
    if (mimeType.startsWith("audio/")) return "audio";
    if (
      mimeType.includes("pdf") ||
      mimeType.includes("document") ||
      mimeType.includes("text")
    ) {
      return "document";
    }
    return "other";
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => {
      const newAttachments = [...prev];
      if (newAttachments[index].url.startsWith("blob:")) {
        URL.revokeObjectURL(newAttachments[index].url);
      }
      newAttachments.splice(index, 1);
      return newAttachments;
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const handleSend = async () => {
    const trimmedMessage = message.trim();
    if ((!trimmedMessage && attachments.length === 0) || disabled) return;

    setMessage("");
    const sentAttachments = [...attachments];
    setAttachments([]);

    if (isTyping) {
      setIsTyping(false);
      stopTyping(conversationId);
    }
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }

    onCancelReply?.();

    await sendMessage(conversationId, trimmedMessage || "📎 Attachment", {
      attachments:
        sentAttachments.length > 0
          ? sentAttachments.map((a) => ({
              id: a.id,
              type: a.type,
              name: a.name,
              originalName: a.originalName,
              url: a.url,
              size: a.size,
              mimeType: a.mimeType,
            }))
          : undefined,
      replyTo: replyTo?._id,
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="border-t bg-white">
      {/* AI Follow-up Banner */}
      {followUpVisible && (
        <div className="flex items-center justify-between gap-2 px-3 py-1.5 bg-amber-50 border-b border-amber-100 text-amber-700 text-xs">
          <span>No response in 4h+ — ready to follow up?</span>
          <div className="flex gap-1">
            <button
              type="button"
              disabled={aiLoading}
              onClick={() => handleAiSuggest("Write a polite follow-up message to check if there has been any update or response")}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 hover:bg-amber-200 font-medium transition-colors disabled:opacity-50"
            >
              {aiLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              AI Follow-up
            </button>
            <button
              type="button"
              onClick={() => setFollowUpVisible(false)}
              className="px-1.5 py-0.5 rounded hover:bg-amber-200 transition-colors"
            >
              ✕
            </button>
          </div>
        </div>
      )}
      <div className="p-3">
      {/* Reply Preview */}
      {replyTo && (
        <div className="flex items-center gap-2 mb-2 p-2 bg-gray-50 rounded-lg border-l-4 border-blue-500">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-blue-600 font-medium">
              Replying to {replyTo.sender?.displayName || "Unknown"}
            </p>
            <p className="text-sm text-gray-600 truncate">{replyTo.content}</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0"
            onClick={onCancelReply}
          >
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </Button>
        </div>
      )}

      {/* Attachments Preview */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {attachments.map((attachment, index) => (
            <div
              key={index}
              className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border"
            >
              {attachment.type === "image" ? (
                <img
                  src={attachment.url}
                  alt={attachment.name}
                  className="h-10 w-10 object-cover rounded"
                />
              ) : (
                <div className="h-10 w-10 bg-gray-200 rounded flex items-center justify-center">
                  <svg
                    className="h-5 w-5 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {attachment.name}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(attachment.size)}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => removeAttachment(index)}
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFileSelect}
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
        />

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="flex-shrink-0"
          disabled={disabled || isUploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {isUploading ? (
            <svg
              className="animate-spin h-5 w-5 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          ) : (
            <svg
              className="h-5 w-5 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
              />
            </svg>
          )}
        </Button>

        <div className="flex-1">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            disabled={disabled}
            rows={1}
            className={cn(
              "w-full resize-none rounded-lg border border-gray-200 px-4 py-2",
              "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
              "disabled:bg-gray-100 disabled:cursor-not-allowed",
              "text-sm",
            )}
          />
        </div>

        {/* AI Smart Reply button */}
        {conversationContext && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="flex-shrink-0 text-purple-500 hover:text-purple-700 hover:bg-purple-50"
            disabled={disabled || aiLoading}
            title="AI: suggest a reply"
            onClick={() => handleAiSuggest("Reply to the last message in a helpful and concise way")}
          >
            {aiLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
          </Button>
        )}

        <Button
          type="button"
          onClick={handleSend}
          disabled={disabled || (!message.trim() && attachments.length === 0)}
          className="flex-shrink-0"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
            />
          </svg>
        </Button>
      </div>

      <p className="text-xs text-gray-400 mt-1 ml-10">
        Press Enter to send, Shift+Enter for new line
      </p>
      </div>{/* end inner padding div */}
    </div>
  );
}
